import { DraggableAttributes, useDraggable } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { CSS } from '@dnd-kit/utilities';
import DragIndicator from '@mui/icons-material/DragIndicator';
import Event from '@mui/icons-material/Event';
import Flag from '@mui/icons-material/Flag';
import { inputBaseClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import { t } from 'i18next';
import React, { CSSProperties, useCallback, useMemo, useState } from 'react';

import GpsStatusPopover from '~components/Common/GpsStatusPopover';
import LabelList from '~components/Labels/LabelList';
import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import EquipmentTypeStatRows from '~components/TruckCapacity/EquipmentTypeStatRows';
import TrucksAssignedCount from '~components/TruckCapacity/TrucksAssignedCount';
import { FeatureFlags } from '~constants/featureFlags';
import {
  AccountCapacityTypeahead,
  AccountDayEquipmentTypeStat,
} from '~hooks/useAccount/models';
import { useCompanyShares } from '~hooks/useCompanyShares/useCompanyShares';
import { DriverTypeahead } from '~hooks/useDrivers/models';
import { useDrivers } from '~hooks/useDrivers/useDrivers';
import { useStores } from '~store';
import { getDriverInfoForCards } from '~utils/drivers/driver-utils';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { nullableStringOrNumberIsValidNumber } from '~utils/utilFunctions';

import { buildAssigneeDragId } from './assigneeDragId';
import {
  DRIVER_PANEL_DRIVER_CARD_HEIGHT_IN_PX,
  DRIVER_PANEL_LABEL_CHIP_HEIGHT_IN_PX,
  VENDOR_CARD_HEADER_BODY_GAP_IN_PX,
  VENDOR_CARD_HEADER_HEIGHT_IN_PX,
  VENDOR_CARD_HEADER_STACK_GAP_IN_PX,
  VENDOR_CARD_LABELS_ROW_HEIGHT_IN_PX,
  VENDOR_CARD_ROW_VERTICAL_PADDING_IN_PX,
  VENDOR_CARD_VERTICAL_BORDER_IN_PX,
  VENDOR_CARD_VERTICAL_PADDING_IN_PX,
  VENDOR_TYPE_SUB_CARD_GAP_IN_PX,
  VENDOR_TYPE_SUB_CARD_HEIGHT_IN_PX,
} from './constants';
import { JOB_COLUMN_WIDTH } from './drivers/constants';

const CARD_BOX_SHADOW = '0px 1px 6px 0px rgba(0, 0, 0, 0.20)';

export enum DriverPanelAssigneeCardPlacement {
  DRAGGING = 'dragging',
  DRIVER_PANEL = 'driver_panel',
}

interface DriverPanelAssigneeCardProps {
  assignee: DriverTypeahead | AccountCapacityTypeahead;
  isEditVendorsMode?: boolean;
  onEditDriver?: (driver: DriverTypeahead) => void;
  onEditVendor?: (vendor: AccountCapacityTypeahead) => void;
  onVendorAvailabilityChange?: (vendorId: string, isAvailable: boolean) => void;
  onVendorTruckCountChange?: (vendorId: string, truckCount: number) => void;
  placement?: `${DriverPanelAssigneeCardPlacement}`;
  style?: CSSProperties;
  usesVendorTypeSubCards?: boolean;
}

function hasVendorTypeSubCards(
  assignee: DriverTypeahead | AccountCapacityTypeahead | undefined,
): assignee is AccountCapacityTypeahead {
  return (
    assignee instanceof AccountCapacityTypeahead && assignee.equipmentTypeStats.length > 0
  );
}

export function getVendorPanelItemSize(
  assignee: DriverTypeahead | AccountCapacityTypeahead | undefined,
) {
  if (!hasVendorTypeSubCards(assignee)) {
    return DRIVER_PANEL_DRIVER_CARD_HEIGHT_IN_PX;
  }

  const typedRowsCount = assignee.equipmentTypeStats.length;

  const labelsRowHeight =
    assignee.labels.length > 0 ? VENDOR_CARD_LABELS_ROW_HEIGHT_IN_PX : 0;

  return (
    VENDOR_CARD_ROW_VERTICAL_PADDING_IN_PX +
    VENDOR_CARD_VERTICAL_BORDER_IN_PX +
    VENDOR_CARD_VERTICAL_PADDING_IN_PX +
    VENDOR_CARD_HEADER_HEIGHT_IN_PX +
    labelsRowHeight +
    VENDOR_CARD_HEADER_BODY_GAP_IN_PX +
    typedRowsCount * VENDOR_TYPE_SUB_CARD_HEIGHT_IN_PX +
    (typedRowsCount - 1) * VENDOR_TYPE_SUB_CARD_GAP_IN_PX
  );
}

export default function DriverPanelAssigneeCard(props: DriverPanelAssigneeCardProps) {
  const { assignee, onEditVendor, style, usesVendorTypeSubCards } = props;
  const showsVendorTypeSubCards =
    Boolean(usesVendorTypeSubCards) &&
    props.placement !== DriverPanelAssigneeCardPlacement.DRAGGING &&
    hasVendorTypeSubCards(assignee);

  if (showsVendorTypeSubCards) {
    return (
      <Box style={style} sx={{ width: `${JOB_COLUMN_WIDTH}px` }}>
        <Box
          sx={{
            px: 1.5,
            py: `${VENDOR_CARD_ROW_VERTICAL_PADDING_IN_PX / 2}px`,
            width: '100%',
          }}
        >
          <DriverPanelVendorMasterCard onEditVendor={onEditVendor} vendor={assignee} />
        </Box>
      </Box>
    );
  }

  return <DriverPanelDraggableAssigneeCard {...props} />;
}

function DriverPanelDraggableAssigneeCard({
  assignee,
  isEditVendorsMode,
  onEditDriver,
  onEditVendor,
  onVendorAvailabilityChange,
  onVendorTruckCountChange,
  placement,
  style,
}: DriverPanelAssigneeCardProps) {
  const theme = useTheme();
  const { ordersDispatchStore } = useStores();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: buildAssigneeDragId(assignee.id),
  });
  const showDriverCounts =
    Boolean(ordersDispatchStore.filters.startDate) &&
    Boolean(ordersDispatchStore.filters.endDate) &&
    ordersDispatchStore.filters.startDate === ordersDispatchStore.filters.endDate;

  const draggableStyle: CSSProperties = {
    ...style,
    width: `${JOB_COLUMN_WIDTH}px`,
    ...(transform && { transform: CSS.Transform.toString(transform) }),
  };

  const cardSx = useMemo(() => {
    const sx: SxProps = {
      backgroundColor:
        placement === DriverPanelAssigneeCardPlacement.DRAGGING
          ? theme.brandV2.colors.treadGray8
          : theme.semantic.surfaceRaised,
      border: `1px solid ${theme.brandV2.colors.treadGray7}`,
      borderRadius: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      height: '100%',
      p: 0.8,
      width: '100%',
      '&:hover': { borderColor: theme.brandV2.colors.treadGray3 },
    };

    return sx;
  }, [placement]);

  if (isDragging && placement === DriverPanelAssigneeCardPlacement.DRIVER_PANEL) {
    return null;
  }

  return (
    <Box ref={setNodeRef} style={draggableStyle} {...attributes}>
      <Box
        sx={{
          height: `${DRIVER_PANEL_DRIVER_CARD_HEIGHT_IN_PX}px`,
          position: 'relative',
          px: 1.5,
          py: 0.5,
          width: '100%',
        }}
      >
        {assignee instanceof DriverTypeahead ? (
          <DriverPanelDriverCard
            draggableListeners={listeners}
            driver={assignee}
            onEditDriver={onEditDriver}
            showDriverCounts={showDriverCounts}
            sx={cardSx}
          />
        ) : (
          <DriverPanelVendorCard
            draggableListeners={listeners}
            isEditMode={isEditVendorsMode}
            onAvailabilityChange={onVendorAvailabilityChange}
            onEditVendor={onEditVendor}
            onTruckCountChange={onVendorTruckCountChange}
            vendor={assignee}
            sx={cardSx}
          />
        )}
      </Box>
    </Box>
  );
}

interface DriverPanelDriverCardProps {
  draggableListeners?: SyntheticListenerMap;
  driver: DriverTypeahead;
  onEditDriver?: (driver: DriverTypeahead) => void;
  showDriverCounts?: boolean;
  sx: SxProps;
}

function DriverPanelDriverCard({
  draggableListeners,
  driver,
  onEditDriver,
  showDriverCounts,
  sx,
}: DriverPanelDriverCardProps) {
  const theme = useTheme();
  const hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.hideDriversVendorNameIfOwnerOperator,
  });

  const driverInfo = getDriverInfoForCards(
    driver,
    driver.companyShare?.senderCompany,
    hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled,
  );
  const isExternalDriver = !!driver.companyShare;
  const { userStore } = useStores();
  const currentCompany = userStore.currentCompanies[0] || userStore.userCompany;
  const { getDriverDeviceStatus } = useDrivers();
  const [isAvailable, setIsAvailable] = useState(driverInfo.isAvailable);

  const { markDriverAsAvailable, markDriverAsUnavailable, isLoading } = useDrivers();
  const { updateCompanyShare, isUpdating } = useCompanyShares();

  const isUpdatingAvailability = isUpdating || isLoading;

  const toggleDriverAvailability = () => {
    const newAvailability = !isAvailable;

    if (isExternalDriver) {
      updateCompanyShare({
        companyShareId: driver.companyShare.id,
        schedule_on: newAvailability,
      }).then(() => setIsAvailable(newAvailability));
    } else {
      const action = newAvailability ? markDriverAsAvailable : markDriverAsUnavailable;
      action(driver.id).then(() => setIsAvailable(newAvailability));
    }
  };

  return (
    <Box
      sx={{
        ...sx,
        alignItems: 'center',
        bgcolor: isAvailable
          ? theme.semantic.surfaceRaised
          : theme.brandV2.colors.treadGray7,
        boxShadow: isAvailable ? CARD_BOX_SHADOW : undefined,
        cursor: 'grab',
        display: 'flex',
        flexDirection: 'row',
        position: 'relative',
        userSelect: 'none',
      }}
      {...draggableListeners}
    >
      <Box
        sx={{
          alignItems: 'center',
          color: theme.brandV2.colors.treadGray4,
          display: 'flex',
          flexDirection: 'column',
          mr: 1,
        }}
      >
        {driver.compliant === false && (
          <Tooltip title={t('administration.compliance_document_types.non_compliant')}>
            <Flag
              sx={{
                color: theme.palette.error.main,
                fontSize: 14,
                mb: 0.25,
              }}
            />
          </Tooltip>
        )}

        <DragIndicator />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          gap: 0,
          overflow: 'hidden',
        }}
      >
        <OverflowAwareText
          variant="body2"
          sx={{
            color: isAvailable
              ? theme.brandV2.colors.treadBlue2
              : theme.brandV2.colors.treadBlack,
            cursor: onEditDriver ? 'pointer' : 'auto',
            fontWeight: 600,
            width: 'auto',
          }}
          data-no-dnd="true"
          onClick={onEditDriver ? () => onEditDriver(driver) : undefined}
        >
          {driverInfo.equipmentName && (
            <>
              <Typography
                component="span"
                sx={{
                  color: theme.brandV2.colors.treadBlack,
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                }}
              >
                {driverInfo.equipmentName}
              </Typography>
              <Typography
                component="span"
                sx={{
                  color: theme.brandV2.colors.treadBlack,
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                }}
              >
                {' • '}
              </Typography>
            </>
          )}

          <Typography
            component="span"
            sx={{
              color: 'inherit',
              fontSize: 'inherit',
              fontWeight: 'inherit',
              textDecoration: onEditDriver ? 'underline' : undefined,
            }}
          >
            {driverInfo.fullName}
          </Typography>
        </OverflowAwareText>

        {driverInfo.truckType && (
          <OverflowAwareText
            sx={{ color: theme.brandV2.colors.treadGray2, fontSize: '11px' }}
          >
            {driverInfo.truckType}
          </OverflowAwareText>
        )}

        {driverInfo.vendorName && (
          <OverflowAwareText
            sx={{ color: theme.brandV2.colors.treadGray2, fontSize: '11px' }}
          >
            {driverInfo.vendorName}
          </OverflowAwareText>
        )}

        <LabelList compact labels={driver.labels} />

        {driver.companyShare?.expiresAt && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Event
              sx={{
                color: theme.palette.brandV2OrangeDark.main,
                fontSize: '11px',
              }}
            />
            <Typography
              sx={{
                color: theme.brandV2.colors.treadGray2,
                fontSize: '11px',
                fontWeight: 400,
              }}
            >
              {t('dispatch.drivers.daily_share')}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {showDriverCounts && Boolean(driver.companyDayDriverStat?.jobsCount) && (
            <Chip
              label={driver.companyDayDriverStat?.jobsCount}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '12px',
                bgcolor: theme.brandV2.colors.treadGray1,
                color: theme.semantic.textInverse,
                height: 20,
              }}
            />
          )}

          {driver.gpsStatus && (
            <GpsStatusPopover
              gpsStatus={driver.gpsStatus}
              onGetDeviceStatus={() =>
                getDriverDeviceStatus(driver.id, currentCompany.id)
              }
            />
          )}
        </Box>
        <Switch
          checked={isAvailable}
          size="small"
          disabled={isUpdatingAvailability}
          onChange={toggleDriverAvailability}
          data-no-dnd="true"
          data-ui={`page-dispatch__driver-panel__driver-${driver.id}__availability-toggle`}
        />
      </Box>
    </Box>
  );
}

interface DriverPanelVendorCardProps {
  draggableListeners?: SyntheticListenerMap;
  isEditMode?: boolean;
  onAvailabilityChange?: (vendorId: string, isAvailable: boolean) => void;
  onEditVendor?: (vendor: AccountCapacityTypeahead) => void;
  onTruckCountChange?: (vendorId: string, truckCount: number) => void;
  sx?: SxProps;
  vendor: AccountCapacityTypeahead;
}

function DriverPanelVendorCard({
  draggableListeners,
  isEditMode,
  onAvailabilityChange,
  onEditVendor,
  onTruckCountChange,
  sx,
  vendor,
}: DriverPanelVendorCardProps) {
  const theme = useTheme();
  const [truckCount, setTruckCount] = useState(String(vendor.currentCapacityCount || ''));
  const [isAvailable, setIsAvailable] = useState(vendor.scheduleOn);
  const isTypedTruckCapacityEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.typedTruckCapacityV1,
  });
  const showEquipmentTypeStats =
    isTypedTruckCapacityEnabled && vendor.equipmentTypeStats.length > 0;

  const isValidTruckCount = useMemo(() => {
    const value = truckCount.trim();

    if (value.length) {
      const isValidNumber = nullableStringOrNumberIsValidNumber(value);

      if (isValidNumber) {
        const valueAsNumber = Number(value);

        return valueAsNumber >= 0 && Number.isSafeInteger(valueAsNumber);
      }

      return false;
    }

    return true;
  }, [truckCount]);

  const handleTruckCountChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.trim();

      setTruckCount(value);

      let isValid = nullableStringOrNumberIsValidNumber(value);

      if (isValid) {
        const valueAsNumber = Number(value);

        isValid = valueAsNumber >= 0 && Number.isSafeInteger(valueAsNumber);

        if (isValid) {
          onTruckCountChange?.(vendor.id, valueAsNumber);
        }
      }
    },
    [onTruckCountChange, vendor.id],
  );

  const handleAvailabilityChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;

      setIsAvailable(checked);
      onAvailabilityChange?.(vendor.id, checked);
    },
    [onAvailabilityChange, vendor.id],
  );

  let content;

  if (isEditMode) {
    content = (
      <>
        <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column', width: '230px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <OverflowAwareText
              sx={{
                color: isAvailable
                  ? theme.brandV2.colors.treadBlue2
                  : theme.brandV2.colors.treadBlack,
                fontWeight: 600,
                width: 'auto',
              }}
            >
              {vendor.name}
            </OverflowAwareText>
          </Box>

          {showEquipmentTypeStats ? (
            <EquipmentTypeStatRows equipmentTypeStats={vendor.equipmentTypeStats} />
          ) : (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: theme.brandV2.colors.treadGray2 }}>
                {t('form_fields.truck_capacity')}
              </Typography>

              <TextField
                error={!isValidTruckCount}
                onChange={handleTruckCountChange}
                type="number"
                value={truckCount}
                sx={{
                  width: '50px',
                  [`& .${inputBaseClasses.input}`]: {
                    bgcolor: theme.semantic.surfaceRaised,
                    border: `1px solid ${theme.brandV2.colors.treadGray3}`,
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: '3px',
                  },
                }}
                data-ui={`page-dispatch__driver-panel__vendor-${vendor.id}__truck-capacity-input`}
              />
            </Box>
          )}
        </Box>

        <Switch
          checked={isAvailable}
          size="small"
          onChange={handleAvailabilityChange}
          data-ui={`page-dispatch__driver-panel__vendor-${vendor.id}__availability-toggle`}
        />
      </>
    );
  } else {
    content = (
      <>
        <Box
          sx={{
            alignItems: 'center',
            color: theme.brandV2.colors.treadGray4,
            display: 'flex',
            flexDirection: 'column',
            mr: 1,
          }}
        >
          {vendor.compliant === false && (
            <Tooltip title={t('administration.compliance_document_types.non_compliant')}>
              <Flag
                sx={{
                  color: theme.palette.error.main,
                  fontSize: 14,
                  mb: 0.25,
                }}
              />
            </Tooltip>
          )}

          <DragIndicator />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            justifyContent: 'start',
            width: '210px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <OverflowAwareText
              sx={{
                color: isAvailable
                  ? theme.brandV2.colors.treadBlue2
                  : theme.brandV2.colors.treadBlack,
                cursor: onEditVendor ? 'pointer' : 'auto',
                fontWeight: 600,
                width: 'auto',
                textDecoration: onEditVendor ? 'underline' : undefined,
              }}
              data-no-dnd="true"
              onClick={onEditVendor ? () => onEditVendor(vendor) : undefined}
            >
              {vendor.name}
            </OverflowAwareText>
          </Box>

          <TrucksAssignedCount
            assignedCount={vendor.currentAssignedCount}
            totalCount={vendor.currentCapacityCount}
          />

          <LabelList compact labels={vendor.labels} />
        </Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        ...sx,
        alignItems: 'center',
        bgcolor: isAvailable
          ? theme.semantic.surfaceRaised
          : theme.brandV2.colors.treadGray7,
        boxShadow: isAvailable ? CARD_BOX_SHADOW : undefined,
        cursor: isEditMode ? 'auto' : 'grab',
        display: 'flex',
        flexDirection: 'row',
        gap: 0.5,
        justifyContent: 'start',
        position: 'relative',
      }}
      {...(isEditMode ? {} : draggableListeners)}
    >
      {content}
    </Box>
  );
}

interface DriverPanelVendorMasterCardProps {
  onEditVendor?: (vendor: AccountCapacityTypeahead) => void;
  vendor: AccountCapacityTypeahead;
}

function DriverPanelVendorMasterCard({
  onEditVendor,
  vendor,
}: DriverPanelVendorMasterCardProps) {
  const theme = useTheme();
  const isFull = vendor.currentAssignedCount >= vendor.currentCapacityCount;
  const isAvailable = vendor.scheduleOn && !isFull;

  return (
    <Box
      sx={{
        bgcolor: isAvailable
          ? theme.semantic.surfaceRaised
          : theme.brandV2.colors.treadGray7,
        border: `1px solid ${theme.brandV2.colors.treadGray7}`,
        borderRadius: '12px',
        boxShadow: isAvailable ? CARD_BOX_SHADOW : undefined,
        display: 'flex',
        flexDirection: 'column',
        gap: `${VENDOR_CARD_HEADER_BODY_GAP_IN_PX}px`,
        px: 1,
        py: `${VENDOR_CARD_VERTICAL_PADDING_IN_PX / 2}px`,
        width: '100%',
        '&:hover': { borderColor: theme.brandV2.colors.treadGray3 },
      }}
      data-test-id={`page-dispatch__driver-panel__vendor-${vendor.id}__card`}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${VENDOR_CARD_HEADER_STACK_GAP_IN_PX}px`,
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
            height: `${VENDOR_CARD_HEADER_HEIGHT_IN_PX}px`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', width: 14 }}>
            {vendor.compliant === false && (
              <Tooltip
                title={t('administration.compliance_document_types.non_compliant')}
              >
                <Flag sx={{ color: theme.palette.error.main, fontSize: 14 }} />
              </Tooltip>
            )}
          </Box>

          <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', minWidth: 0 }}>
            <OverflowAwareText
              sx={{
                color: isAvailable
                  ? theme.brandV2.colors.treadBlue2
                  : theme.brandV2.colors.treadBlack,
                cursor: onEditVendor ? 'pointer' : 'auto',
                fontWeight: 600,
                lineHeight: '16px',
                textDecoration: onEditVendor ? 'underline' : undefined,
                width: 'auto',
              }}
              onClick={onEditVendor ? () => onEditVendor(vendor) : undefined}
            >
              {vendor.name}
            </OverflowAwareText>
            <Typography
              sx={{
                color: theme.brandV2.colors.treadGray2,
                fontSize: '11px',
                lineHeight: '16px',
              }}
            >
              {t('dispatch.drivers.truck_types_count', {
                count: vendor.equipmentTypeStats.length,
              })}
            </Typography>
          </Box>

          <TrucksAssignedCount
            assignedCount={vendor.currentAssignedCount}
            stacked
            totalCount={vendor.currentCapacityCount}
          />
        </Box>

        {vendor.labels.length > 0 && (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 0.5,
              height: DRIVER_PANEL_LABEL_CHIP_HEIGHT_IN_PX,
            }}
          >
            <LabelList compact labels={vendor.labels} />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${VENDOR_TYPE_SUB_CARD_GAP_IN_PX}px`,
        }}
      >
        {vendor.equipmentTypeStats.map((equipmentTypeStat) => (
          <DriverPanelVendorTypeSubCard
            key={equipmentTypeStat.id}
            isAvailable={isAvailable}
            placement={DriverPanelAssigneeCardPlacement.DRIVER_PANEL}
            equipmentTypeStat={equipmentTypeStat}
            vendorId={vendor.id}
          />
        ))}
      </Box>
    </Box>
  );
}

interface DriverPanelVendorTypeSubCardProps {
  isAvailable: boolean;
  placement: `${DriverPanelAssigneeCardPlacement}`;
  equipmentTypeStat: AccountDayEquipmentTypeStat;
  vendorId: string;
}

export function DriverPanelVendorTypeSubCard(props: DriverPanelVendorTypeSubCardProps) {
  // The panel copy stays mounted during a drag, so a second useDraggable under the same
  // id here would hand dnd-kit the overlay's node and drop the id when it unmounts.
  if (props.placement === DriverPanelAssigneeCardPlacement.DRAGGING) {
    return <VendorTypeSubCardBody {...props} isDragOverlay />;
  }

  return <DraggableVendorTypeSubCard {...props} />;
}

function DraggableVendorTypeSubCard(props: DriverPanelVendorTypeSubCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: buildAssigneeDragId(props.vendorId, props.equipmentTypeStat.equipmentType.id),
  });

  return (
    <VendorTypeSubCardBody
      {...props}
      dragHandleProps={{ ...attributes, ...listeners }}
      isDragOverlay={false}
      isDraggingSourceInPanel={isDragging}
      setNodeRef={setNodeRef}
    />
  );
}

interface VendorTypeSubCardBodyProps extends DriverPanelVendorTypeSubCardProps {
  dragHandleProps?: DraggableAttributes | SyntheticListenerMap;
  isDragOverlay: boolean;
  isDraggingSourceInPanel?: boolean;
  setNodeRef?: (element: HTMLElement | null) => void;
}

function VendorTypeSubCardBody({
  dragHandleProps,
  isAvailable,
  isDragOverlay,
  isDraggingSourceInPanel = false,
  setNodeRef,
  equipmentTypeStat,
  vendorId,
}: VendorTypeSubCardBodyProps) {
  const theme = useTheme();

  return (
    <Box
      ref={setNodeRef}
      sx={{
        alignItems: 'center',
        bgcolor:
          isDragOverlay || !isAvailable
            ? theme.brandV2.colors.treadGray8
            : theme.brandV2.colors.treadGray9,
        border: `1px solid ${theme.brandV2.colors.treadGray7}`,
        borderRadius: '8px',
        cursor: 'grab',
        display: 'flex',
        gap: 0.5,
        height: `${VENDOR_TYPE_SUB_CARD_HEIGHT_IN_PX}px`,
        p: '6px 8px 6px 4px',
        userSelect: 'none',
        visibility: isDraggingSourceInPanel ? 'hidden' : 'visible',
        width: '100%',
      }}
      {...dragHandleProps}
      data-test-id={`page-dispatch__driver-panel__vendor-${vendorId}__truck-type-${equipmentTypeStat.equipmentType.id}__card`}
    >
      <DragIndicator sx={{ color: theme.brandV2.colors.treadGray4 }} />

      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', minWidth: 0 }}>
        <OverflowAwareText sx={{ fontWeight: 600, lineHeight: '16px' }}>
          {equipmentTypeStat.equipmentType.name}
        </OverflowAwareText>
        <TrucksAssignedCount
          assignedCount={equipmentTypeStat.assignedCount}
          sx={{ fontSize: '12px', lineHeight: '16px' }}
          totalCount={equipmentTypeStat.committedCount}
        />
      </Box>
    </Box>
  );
}
